import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  ExpandMore,
  FiberManualRecord,
  FiberManualRecordOutlined,
} from "@mui/icons-material";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useQuickLinksItems } from "../../context/QuickLinksContext";

const DraggableItem = ({
  tab,
  id,
  setSelectedTab,
  selectedTab,
  index,
  data,
  isLast,
}) => {
  const { attributes, listeners, setNodeRef, transition } = useSortable({
    id: tab.id,
  });

  const { setSessionTab } = useQuickLinksItems();

  return (
    <ListItem
      disablePadding
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{ transition }}
    >
      <ListItemButton
        onClick={() => {
          setSelectedTab(tab.id);
          if (tab.id === "exchange") {
            setSessionTab("main");
          }
        }}
        sx={{
          mx: 2,
          borderBottom: isLast ? "none" : "1px solid #E6EBF0",
          "&:hover": { backgroundColor: "rgba(41, 64, 90, 0.04)" },
          borderRadius: "8px",
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: "40px",
            color: selectedTab === tab.id ? "#29405A" : "#33261D",
          }}
        >
          {selectedTab === tab.id ? (
            <FiberManualRecord />
          ) : (
            <FiberManualRecordOutlined />
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              sx={{
                fontFamily: "'Quicksand', sans-serif",
                fontWeight: selectedTab === tab.id ? 700 : 500,
                fontSize: "13px",
                color: selectedTab === tab.id ? "#29405A" : "#33261D",
                letterSpacing: "0.2px",
              }}
            >
              {tab.name}
            </Typography>
          }
        />
      </ListItemButton>
    </ListItem>
  );
};

const SideBar = ({ data, setSelectedTab, selectedTab, primaryTabs, secondaryTabs }) => {
  const [expanded, setExpanded] = useState(true);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const { adminTabs } = useQuickLinksItems();

  return (
    <Box
      sx={{
        width: "275px",
        backgroundColor: "#FFFFFF",
        borderRadius: "24px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      }}
    >
      {/* ── Primary Navigation ── */}
      <SortableContext
        items={primaryTabs?.map((tab) => tab.id) || []}
        strategy={verticalListSortingStrategy}
      >
        <List>
          {primaryTabs?.map((tab, index) => (
            <DraggableItem
              key={tab.id}
              index={index}
              data={primaryTabs}
              tab={tab}
              id={tab.id}
              setSelectedTab={setSelectedTab}
              selectedTab={selectedTab}
              isLast={index === primaryTabs.length - 1}
            />
          ))}
        </List>
      </SortableContext>

      {/* ── Secondary: Learning & Resources (collapsible) ── */}
      {secondaryTabs?.length > 0 && (
        <Accordion
          expanded={expanded}
          onChange={() => setExpanded((prev) => !prev)}
          disableGutters
          elevation={0}
          sx={{
            backgroundColor: "transparent",
            "&:before": { display: "none" },
            borderTop: "1px solid #E6EBF0",
            mx: 1,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore sx={{ color: "#29405A" }} />}
            sx={{
              minHeight: "44px",
              px: 1,
              "& .MuiAccordionSummary-content": {
                my: "8px",
              },
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Quicksand', sans-serif",
                fontWeight: 700,
                fontSize: "12px",
                color: "#29405A",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
              }}
            >
              Learning & Resources
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <SortableContext
              items={secondaryTabs?.map((tab) => tab.id) || []}
              strategy={verticalListSortingStrategy}
            >
              <List disablePadding>
                {secondaryTabs?.map((tab, index) => (
                  <DraggableItem
                    key={tab.id}
                    index={index}
                    data={secondaryTabs}
                    tab={tab}
                    id={tab.id}
                    setSelectedTab={setSelectedTab}
                    selectedTab={selectedTab}
                    isLast={index === secondaryTabs.length - 1}
                  />
                ))}
              </List>
            </SortableContext>
          </AccordionDetails>
        </Accordion>
      )}

      {/* ── Admin (role: admin only) ── */}
      {adminTabs?.length > 0 && (
        <Accordion
          expanded={adminExpanded}
          onChange={() => setAdminExpanded((prev) => !prev)}
          disableGutters
          elevation={0}
          sx={{
            backgroundColor: "transparent",
            "&:before": { display: "none" },
            borderTop: "1px solid #E6EBF0",
            mx: 1,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore sx={{ color: "#29405A" }} />}
            sx={{
              minHeight: "44px",
              px: 1,
              "& .MuiAccordionSummary-content": { my: "8px" },
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Quicksand', sans-serif",
                fontWeight: 700,
                fontSize: "12px",
                color: "#29405A",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
              }}
            >
              Admin
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <SortableContext
              items={adminTabs?.map((tab) => tab.id) || []}
              strategy={verticalListSortingStrategy}
            >
              <List disablePadding>
                {adminTabs?.map((tab, index) => (
                  <DraggableItem
                    key={tab.id}
                    index={index}
                    data={adminTabs}
                    tab={tab}
                    id={tab.id}
                    setSelectedTab={setSelectedTab}
                    selectedTab={selectedTab}
                    isLast={index === adminTabs.length - 1}
                  />
                ))}
              </List>
            </SortableContext>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default SideBar;
